import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import {
  OtherReportActionButtons,
  ReportDateRangeFields,
  OtherReportDocument,
  OtherReportFilterCard,
  OtherReportFilterSection,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportPlaceholder,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportSectionBlock,
  OtherReportShell,
  OtherReportTable,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const BankStatementReport = (props) => {
  const location = useLocation();
  const isOtherReportsRoute = location.pathname.startsWith('/other-reports/');
  const [meta, setMeta] = useState({ banks: [] });
  const [inputValues, setInputValues] = useState({ bank_name: [], summary: false });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/bank_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Bank Statement', error.message, 'error'));
  }, []);

  const toggleBank = (id) => {
    const selected = new Set((inputValues.bank_name || []).map(String));
    const key = String(id);
    if (selected.has(key)) {
      selected.delete(key);
    } else {
      selected.add(key);
    }
    setInputValues({ ...inputValues, bank_name: Array.from(selected) });
  };

  const setBanks = (ids) => {
    setInputValues({ ...inputValues, bank_name: (ids || []).map(String) });
  };

  const loadReport = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }

    if (!inputValues.bank_name?.length) {
      swal('Please select at least one bank account', '', 'warning');
      return;
    }

    setLoading(true);
    setReport(null);

    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      bank_name: inputValues.bank_name,
    };

    if (inputValues.summary) {
      payload.summary = 1;
    }

    try {
      const data = await fetchReportJson(`${API_URL}/bank_statement`, payload);
      setReport(data);
    } catch (error) {
      swal('Bank Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      start_date: inputValues.start_date,
      end_date: inputValues.end_date,
      bank_name: inputValues.bank_name,
    };
    if (inputValues.summary) {
      payload.summary = 1;
    }
    return payload;
  };

  const downloadPdf = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }
    if (!inputValues.bank_name?.length) {
      swal('Please select at least one bank account', '', 'warning');
      return;
    }
    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/other-reports/bank_statement/pdf`,
        buildPayload(),
        'bank_statement.pdf',
      );
    } catch (error) {
      swal('Bank Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const renderDetailedRows = (section) => (
    <>
      {(section.rows || []).map((row, index) => {
        if (row.row_type === 'bank_header') {
          return (
            <tr key={`bank-header-${index}`}>
              <td colSpan="6" className="fw-bold">{row.bank_name}</td>
            </tr>
          );
        }

        return (
          <tr key={`row-${index}`} className={row.row_type === 'opening' ? 'other-report-opening-row' : ''}>
            <td>{formatReportDate(row.ac_date)}</td>
            <td>{row.account_label}</td>
            <td>{row.description}</td>
            <td className="text-end">{reportNumberFormat(row.dr_amount)}</td>
            <td className="text-end">{reportNumberFormat(row.cr_amount)}</td>
            <td className="text-end">{reportNumberFormat(row.outstanding)}</td>
          </tr>
        );
      })}
      {section.totals && (
        <tr className="other-report-total-row">
          <td colSpan="3" className="text-end">Totals</td>
          <td className="text-end">{reportNumberFormat(section.totals.total_dr)}</td>
          <td className="text-end">{reportNumberFormat(section.totals.total_cr)}</td>
          <td className="text-end">{reportNumberFormat(section.totals.outstanding)}</td>
        </tr>
      )}
    </>
  );

  const renderSummaryRows = (section) => (
    <>
      {(section.rows || []).map((row, index) => (
        <tr key={`summary-${index}`}>
          <td>{formatReportDate(row.ac_date)}</td>
          <td>{row.bank_name}</td>
          <td className="text-end">{reportNumberFormat(row.total_dr)}</td>
          <td className="text-end">{reportNumberFormat(row.total_cr)}</td>
          <td className="text-end">{reportNumberFormat(row.balance)}</td>
        </tr>
      ))}
      {section.totals && (
        <tr className="other-report-total-row">
          <td colSpan="2" className="text-end">Totals</td>
          <td className="text-end">{reportNumberFormat(section.totals.total_dr)}</td>
          <td className="text-end">{reportNumberFormat(section.totals.total_cr)}</td>
          <td className="text-end">{reportNumberFormat(section.totals.outstanding)}</td>
        </tr>
      )}
    </>
  );

  const sections = report?.is_summary ? (report.summary_sections || []) : (report?.sections || []);
  const reportTitle = report?.category || 'Bank Statement';
  const reportMetaLine = report
    ? formatReportDateRange(report.start_date, report.end_date)
    : '';
  const rowCount = sections.reduce(
    (sum, section) => sum + (section.rows || []).filter((row) => row.row_type !== 'bank_header').length,
    0,
  );

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        {isOtherReportsRoute ? (
          <Sidebar_Progress props={props} progress={22} />
        ) : (
          <Sidebar_Progress props={props} progress={14} operation={4} op={4} />
        )}
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Bank Statement" subtitle={meta.pump_name} />

            <OtherReportFilterCard>
              <OtherReportToolbar
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <div className="other-report-field">
                      <label>Report mode</label>
                      <div className="form-check other-report-checkbox-inline">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="bank-summary-toggle"
                          checked={Boolean(inputValues.summary)}
                          onChange={(e) => setInputValues({ ...inputValues, summary: e.target.checked })}
                        />
                        <label className="form-check-label ms-2 mb-0" htmlFor="bank-summary-toggle">
                          Summary only
                        </label>
                      </div>
                    </div>
                  </>
                )}
              />

              <OtherReportFilterSection
                title="Bank accounts"
                description="Select one or more bank accounts to include in the statement."
              >
                <OtherReportSearchableCheckList
                  items={(meta.banks || []).map((bank) => ({
                    id: bank.id,
                    label: `${bank.ac_type_name || ''} ${bank.bank_name || ''}`.trim(),
                  }))}
                  selectedIds={inputValues.bank_name || []}
                  onToggle={toggleBank}
                  onChange={setBanks}
                  searchPlaceholder="Search banks…"
                />
              </OtherReportFilterSection>
            </OtherReportFilterCard>

            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating report" />}

              {!loading && !report && <OtherReportPlaceholder />}

              {!loading && report && (
              <OtherReportDocument
                title={reportTitle}
                subtitle={report.pump_name}
                meta={[reportMetaLine, report.bank_name_head].filter(Boolean).join(' · ')}
                rowCount={rowCount}
              >
                {sections.map((section, sectionIndex) => (
                  <OtherReportSectionBlock key={`section-${sectionIndex}`} title={section.title}>
                    <OtherReportTable
                      columns={report.is_summary ? [
                        { key: 'date', label: 'Date' },
                        { key: 'bank', label: 'Bank name' },
                        { key: 'debit', label: 'Total debit', align: 'right' },
                        { key: 'credit', label: 'Total credit', align: 'right' },
                        { key: 'balance', label: 'Balance', align: 'right' },
                      ] : [
                        { key: 'date', label: 'Date' },
                        { key: 'head', label: 'Account heads' },
                        { key: 'description', label: 'Description' },
                        { key: 'debit', label: 'Debit (Rs)', align: 'right' },
                        { key: 'credit', label: 'Credit (Rs)', align: 'right' },
                        { key: 'outstanding', label: 'Outstanding (Rs)', align: 'right' },
                      ]}
                      footer={sectionIndex === sections.length - 1 ? (
                        <span>{rowCount} transaction rows · {report.is_summary ? 'Summary view' : 'Detailed view'}</span>
                      ) : null}
                    >
                      <tbody>
                        {report.is_summary ? renderSummaryRows(section) : renderDetailedRows(section)}
                      </tbody>
                    </OtherReportTable>
                  </OtherReportSectionBlock>
                ))}
              </OtherReportDocument>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default BankStatementReport;
